import { Component, OnInit, Inject } from '@angular/core';
import { ProjectSubmodule } from '../../../../resources/submodule/project-submodule.interface';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormGroup, FormBuilder } from '@angular/forms';
import { ApiService } from '../../../../api/api.service';
import { take } from 'rxjs/operators';
import { BuildingTemplateService } from '../../../../resources/building-template/building-template.service';
import { ProjectTemplateService } from '../../../../resources/project-template/project-template.service';
import { ProjectTemplate, ProjectTemplates } from '../../../../resources/project-template/project-template.interface';
import { BuildingTemplates, BuildingTemplate } from '../../../../resources/building-template/building-template.interface';
import { ProjectModule } from '../../../../resources/module/project-module.interface';
import { ProjectElement } from '../../../../resources/element/project-element.interface';
import { ParagraphAddAction } from './paragraph-add.interface';
import { ProjectService } from '../../../../resources/project/project.service';
import { BuildingService } from '../../../../resources/building/building.service';
import { TranslateService } from '../../../../utilities/translate/translate.service';
import { ConstantsQualityLevel } from '../../../../utilities/constants/constants.interface';
import { ConstantsService } from '../../../../utilities/constants/constants.service';

/**
 * Add new paragraph to document.
 *
 * Types:
 *  - Empty paragraph. Defaults to project element.
 *  - Import from template. Can be imported from both Project and Building templates.
 *
 * @note Rename the `RestoreParagraph` from SPEC-1698 component to `ParagraphRestore`,
 *  so they have a more consistent order in the modals folder.
 */
@Component({
  selector: 'sg2-paragraph-add',
  templateUrl: './paragraph-add.component.html',
  styleUrls: ['./paragraph-add.component.less']
})
export class ModalParagraphAddComponent implements OnInit {

  /** Type of elements to choose from, currently supports New and Template. */
  public elementTypes: { _id: number, label: string; }[];

  /** Type of templates to choose from, currently supports Project and Building, both textual. */
  public templateTypes: { _id: number; label: string; }[];

  /** Module/submodule/element tree object. */
  public tree: ProjectModule[];

  /** Referene to correct list of templates (project or building), for quick access. */
  public templates: BuildingTemplates | ProjectTemplates;

  /** Form. */
  public form: FormGroup;

  /** The element's parent submodule. */
  public submodule: ProjectSubmodule;

  /** Form validity. */
  public formValid = true;

  /** Template quality levels. */
  public qualityLevels: ConstantsQualityLevel[];

  /** Selected element in tree view. */
  public selectedElement: ProjectElement;

  constructor(
    @Inject(MAT_DIALOG_DATA) private readonly config: { submodule: ProjectSubmodule },
    // private buildingService: BuildingService, // Circular
    private buildingTemplateService: BuildingTemplateService,
    private constantsService: ConstantsService,
    public dialogRef: MatDialogRef<ModalParagraphAddComponent>,
    private formBuilder: FormBuilder,
    private projectService: ProjectService,
    private projectTemplateService: ProjectTemplateService,
    private translateService: TranslateService
  ) {
    this.submodule = config.submodule;
  }

  ngOnInit() {
    this._initOptions();
    this._initForm();

    this._onElementTypeChange();
    this._onTemplateTypeChange();
    this._onTemplateChange();
  }

  /**
   * Select the element in the tree.
   *
   * Can also be used to deselect an element, by providing `null`.
   *
   * @param element The element to select. Also accepts `null` to deselect.
   */
  public selectElement(element: ProjectElement | null): void {
    if (element) {
      this.selectedElement = element;
      this.formValid = true;
    } else {
      this.selectedElement = null;
      this.formValid = false;
    }
  }

  /**
   * Submit form and add element of choice.
   */
  public submit(): void {
    if (!this.selectedElement) {
      this.dialogRef.close({
        action: 'new'
      } as ParagraphAddAction);
    } else {
      this.dialogRef.close({
        action: 'template',
        element: this._parseTemplateElement(this.selectedElement)
      } as ParagraphAddAction);
    }
  }

  /**
   * Initialize form.
   */
  private _initForm(): void {
    this.form = this.formBuilder.group({
      elementType: 1,
      qualityLevel: [{ value: false, disabled: true }],
      templateType: [{ value: false, disabled: true }],
      template: [{ value: false, disabled: true }],
      templateElementId: null
    });
  }

  /**
   * Set options.
   */
  private _initOptions(): void {
    this.elementTypes = [
      { _id: 1, label: this.translateService.get('newSpecificationEmpty') },
      { _id: 2, label: this.translateService.get('newSpecificationFromTemplate') }
    ];
    this.templateTypes = [
      { _id: 1, label: this.translateService.get('projecttemplate') },
      { _id: 2, label: this.translateService.get('buildingtemplate') }
    ];
    this.qualityLevels = this.constantsService.getQualityLevels();
  }

  /**
   * Parse template element to element which can be added to the project.
   *
   * @param element The template element.
   * @returns Element properties as a base for creating a new element.
   */
  private _parseTemplateElement(element: ProjectElement): Partial<ProjectElement> {
    const projectLevel = this.form.get('templateType').value;
    const qualityLevel = this.qualityLevels.find(q => q._id === this.form.get('qualityLevel').value).value;
    return {
      active: true,
      assignedToUserId: -1,
      constructionId: projectLevel === 1
        ? this.projectService.getId()
        : this.projectService.get().buildings[0]._id,
      hasInputInstruction: false,
      hasMultipleQualityLevels: false,
      isCustom: true,
      inputType: 'Long String',
      knowledgeLevel: 1,
      moduleId: this.config.submodule.moduleId,
      name: element.name,
      projectLevel: projectLevel,
      quality: qualityLevel,
      submoduleId: this.config.submodule._id,
      templateValues: element.values,
      value: element.values.find(v => v.quality === qualityLevel).value,
    };
  }

  /**
   * Track changes to element type form field.
   */
  private _onElementTypeChange(): void {
    this.form.get('elementType').valueChanges.subscribe(value => {
      switch (value) {
        case 1: // Empty element
          this.form.get('templateType').disable();
          this.form.get('template').disable();
          this.form.get('qualityLevel').disable();
          break;
        case 2: // Choose from template
          this.form.get('templateType').enable();
          break;
      }
      this.formValid = value === 1;
      this.selectElement(null);
    });
  }

  /**
   * Track changes to template type (project / building).
   */
  private _onTemplateChange(): void {
    this.form.get('template').valueChanges.subscribe(templateName => {
      if (templateName) {
        const template = this.templates.find(t => t.name === templateName);
        const projectLevel = this.form.get('templateType').value;
        switch (projectLevel) {
          case 1: // Project
            this.tree = this.projectTemplateService.getTemplateTree(template._id);
            break;
          case 2: // Building
            this.tree = this.buildingTemplateService.getTemplateTree(template._id);
            break;
        }
      } else {
        this.tree = null;
      }
      this.selectElement(null);
    });
  }

  /**
   * Track changes to template type form field.
   */
  private _onTemplateTypeChange(): void {
    this.form.get('templateType').valueChanges.subscribe(value => {
      switch (value) {
        case 1: // Project template
          this.templates = this.projectTemplateService.get();
          this.form.get('qualityLevel').setValue(
            this.qualityLevels.find(ql => ql.value === this.projectService.getQualityLevel())._id
          );
          break;
        case 2: // Building template
          this.templates = this.buildingTemplateService.get();
          this.form.get('qualityLevel').setValue(
            // Using the buildingService gives a circular dependency,
            // so check the project's primary building instead.
            this.qualityLevels.find(ql => ql.value === this.projectService.get().buildings[0].qualityLevel)._id
          );
          break;
      }
      if (value) {
        this.form.get('template').setValue(this.templates[0].name);
        this.form.get('template').enable();
        this.form.get('qualityLevel').enable();
      }
      this.selectElement(null);
    });
  }
}
